import PropTypes from '@root/vendor/prop-types';
import React from '@root/vendor/react';
import Tooltip, { tooltipDateFormat } from '@root/joinroot.com/src/components/covid-driving/chart-section/chart-tooltip';
import { Colors } from '@root/core/src/utils/styles';
import { ResponsiveLine } from '@nivo/line';
import { localDateFromString, stringifyDate } from '@root/joinroot.com/src/components/covid-driving/covid-driving-utils';

export default function LineChart({
  metric,
  stayHomeLabel,
  data,
  eventsFileData,
  yAxisMax,
  yAxisTickValues,
  endDate,
  startDate,
}) {
  const getDateTickValues = (chartBegin, chartEnd) => {
    if (data.length < 1) { return; }

    // First make a copy of these dates.
    const firstTick = new Date(chartBegin);
    const lastTick = new Date(chartEnd);

    lastTick.setDate(lastTick.getDate() - 1);

    firstTick.setHours(24, 0, 0, 0);
    lastTick.setHours(0, 0, 0, 0);

    return [firstTick, lastTick];
  };

  const endDateSimple = stringifyDate(endDate);
  const startDateSimple = stringifyDate(startDate);

  const eventMarkers = Object.keys(eventsFileData.national)
    .filter((dateVal) => dateVal > startDateSimple && dateVal < endDateSimple)
    .map((dateVal) => {
      return {
        axis: 'x',
        value: localDateFromString(dateVal),
        lineStyle: {
          stroke: Colors.gray40(),
          strokeWidth: 1,
        },
        legend: null,
      };
    });

  const theme = {
    crosshair: {
      line: {
        stroke: Colors.gray60(),
        strokeDasharray: '4 4',
      },
    },
    axis: {
      ticks: {
        text: {
          fontSize: 14,
          fill: Colors.nearBlack(),
        },
      },
    },
    grid: {
      line: {
        stroke: Colors.gray20(),
      },
    },
  };

  return (
    <ResponsiveLine
      axisBottom={{
        format: '%b %d', // capital B will give us the full month name here.
        tickValues: getDateTickValues(startDate, endDate),
        legend: '',
        legendOffset: 0,
      }}
      axisLeft={{
        orient: 'left',
        tickSize: 0,
        tickPadding: 10,
        tickValues: yAxisTickValues,
        legend: null,
      }}
      axisRight={null}
      axisTop={null}
      colors={(d) => d.color}
      data={data}
      enableArea={true}
      enableGridX={false}
      enablePoints={true}
      margin={{
        top: 10,
        right: 20,
        bottom: 30,
        left: 38,
      }}
      markers={eventMarkers}
      pointBorderColor={Colors.white()}
      pointBorderWidth={2}
      pointColor={{
        from: 'color',
      }}
      pointLabel={'y'}
      pointLabelYOffset={-12}
      pointSize={10}
      theme={theme}
      tooltip={({ point }) => (
        <Tooltip
          eventsFileData={eventsFileData}
          metric={metric}
          point={point}
          stayHomeLabel={stayHomeLabel}
        />
      )}
      useMesh={true}
      xFormat={`time:${tooltipDateFormat}`}
      xScale={{
        type: 'time',
        format: '%Y-%m-%d',
        precision: 'day',
        min: 'auto',
        max: 'auto',
        useUTC: false,
      }}
      yScale={{
        type: 'linear',
        min: 0,
        max: yAxisMax,
        stacked: false,
      }}
    />
  );
}

LineChart.propTypes = {
  data: PropTypes.array,
  endDate: PropTypes.instanceOf(Date),
  eventsFileData: PropTypes.shape({
    national: PropTypes.object,
  }),
  metric: PropTypes.string,
  point: PropTypes.object,
  startDate: PropTypes.instanceOf(Date),
  stayHomeLabel: PropTypes.string,
  yAxisLegend: PropTypes.string,
  yAxisMax: PropTypes.number,
  yAxisTickValues: PropTypes.array,
};
